import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment-timezone';
import { parseAddress } from 'us-address-parser';
import { ToastrService } from 'ngx-toastr';
import { sweetAlert, sweetAlertWithTwoCheckboxes } from '../../utils/swal';
import { getAddressDetails } from '../../utils/utils.common';
import { SharedDataService } from '../../service/shared-service/shared-data.service';
import { MemberService } from '../../component/members/member-service/member.service';
import { CorporateService } from '../../component/corporate-user/corporate-service/corporate-service';
import { LevelOfServiceService } from '../../component/level-of-service/service/level-of-service.service';
import { StagingService } from '../../component/assigned-trips/staging/staging.service';

/**
 * Compact create / edit trip form shown as a slideout on the Staging page.
 * Builds the same trip payload as the full New Trip page and saves it through the MemberLink requests API:
 * POST memberlink/requests (create) / PUT memberlink/requests/:memberAssignId (update).
 */
@Component({
  selector: 'app-staging-trip-form-slideout',
  templateUrl: './staging-trip-form-slideout.component.html',
  styleUrls: ['./staging-trip-form-slideout.component.css']
})
export class StagingTripFormSlideoutComponent implements OnInit, OnDestroy {
  @Input() tripId: string;
  @Output() onClose = new EventEmitter<any>();
  @Output() onSaved = new EventEmitter<any>();

  tripForm: FormGroup;
  user: any;
  timeZone = 'America/New_York';
  minDate = moment().tz(this.timeZone).startOf('day');

  fundingSourceList = [];
  memberList = [];
  levelOfServiceList = [];
  spaceTypeMainList = [];
  spaceTypeList = [];
  selectedMember: any;
  tripDetails: any;

  isLoading = false;
  isSaving = false;
  submitted = false;

  // Change tracking for edit mode extended view
  initialSnapshot: any = null;
  initialLabels: any = {};
  changedFields: Array<{ key: string; label: string; from: string; to: string }> = [];
  isExtendedViewOpen = true;

  // Standing order (same behaviour as the MemberCare Create Trip form)
  readonly weekDays = [
    { label: 'Sun', day: 0 },
    { label: 'Mon', day: 1 },
    { label: 'Tue', day: 2 },
    { label: 'Wed', day: 3 },
    { label: 'Thu', day: 4 },
    { label: 'Fri', day: 5 },
    { label: 'Sat', day: 6 }
  ];
  isStandingOrder = false;
  recurringDays: number[] = [];
  recurringEndDate: any = null;

  constructor(
    private fb: FormBuilder,
    private sharedDataService: SharedDataService,
    private memberService: MemberService,
    private corporateService: CorporateService,
    private levelOfServiceService: LevelOfServiceService,
    private stagingService: StagingService,
    private toastr: ToastrService
  ) { }

  get form() {
    return this.tripForm.controls;
  }

  get legs(): FormArray {
    return this.tripForm.get('tripLegs') as FormArray;
  }

  get returnLeg(): FormGroup {
    return this.legs.length ? (this.legs.at(0) as FormGroup) : null;
  }

  get isEdit(): boolean {
    return !!this.tripId;
  }

  get selectedFundingSource() {
    const id = this.tripForm && this.tripForm.value.cooperate;
    return id ? this.fundingSourceList.find((f) => f._id === id) : null;
  }

  get selectedFundingSourceName(): string {
    return this.selectedFundingSource ? this.selectedFundingSource.displayName : '';
  }

  get selectedSpaceTypeTitle(): string {
    const id = this.tripForm && this.tripForm.value.spaceType;
    const spaceType = id ? this.spaceTypeMainList.find((s) => s._id === id) : null;
    return spaceType ? spaceType.title : '';
  }

  ngOnInit() {
    this.tripForm = this.fb.group({
      company: ['', Validators.required],
      eligibilityStatus: [''],
      jobType: ['cooperate', Validators.required],
      isMedical: [false],
      companyType: [''],

      cooperate: ['', Validators.required],
      cooperateDetails: [{}],
      payorId: [''],
      client: ['', Validators.required],
      los: ['', Validators.required],
      losDetails: [{}],
      spaceType: ['', Validators.required],
      spaceTypeDetails: [{}],

      priorityClient: this.fb.group({
        displayName: ['', Validators.required],
        contactNumber: ['', Validators.required],
        cellPhone: [''],
        clientId: [''],
        email: [''],
        preDob: [''],
        dob: [''],
        weight: [''],
        gender: ['']
      }),

      jobOriginAddress: ['', Validators.required],
      jobOriginLatitude: [0, Validators.required],
      jobOriginLongitude: [0, Validators.required],
      zipCodeOrg: [''],
      cityOrg: [''],
      stateOrg: [''],
      puStreet: [''],

      jobDestinationAddress: ['', Validators.required],
      jobDestinationLatitude: [0, Validators.required],
      jobDestinationLongitude: [0, Validators.required],
      zipCodeDest: [''],
      cityDest: [''],
      stateDest: [''],
      doStreet: [''],

      originAddressAdditional: this.buildAddressAdditional(),
      destinationAddressAdditional: this.buildAddressAdditional(),

      preScheduleTime: ['', Validators.required],
      scheduleTime: ['', Validators.required],
      preAppointmentTime: ['', Validators.required],
      appointmentTime: ['', Validators.required],
      actualAppointmentTime: [''],

      tripLegs: this.fb.array([]),
      addonServices: this.fb.array([]),
      multidates: this.fb.array([]),
      tripReason: [[]],
      companyNote: [''],
      dispatchNoteTwo: [''],
      noOfEscorts: [''],
      milage: [null],
      isWillCall: [false],
      isRecurring: [false],
      isStandingOrder: [false],
      isVipTrip: [false],
      isConfirmedTrip: [false],
      authorizationNumber: [''],
      // Only enabled (and sent) when the user applies address changes to associated trips
      isUpdateSameDayLegs: [{ value: false, disabled: true }],
      isUpdateFutureLegs: [{ value: false, disabled: true }]
    });

    this.tripForm.valueChanges
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(() => {
        this.computeChanges();
      });

    this.isLoading = true;
    forkJoin([
      this.levelOfServiceService.getLevelOfServices({ status: 'active' }),
      this.levelOfServiceService.getSpaceTypes({ status: 'active' }),
      this.corporateService.getCorporateUsers('active')
    ])
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        ([losList, spaceTypes, fundingSources]) => {
          this.levelOfServiceList = losList || [];
          this.spaceTypeMainList = spaceTypes || [];
          this.fundingSourceList = fundingSources || [];
          this.getUser();
        },
        (err) => {
          this.isLoading = false;
          sweetAlert('Error', err, 'error', 'OK');
        }
      );
  }

  buildAddressAdditional() {
    return this.fb.group({
      modifier: [''],
      modifierTwo: [''],
      floor: [''],
      room: [''],
      aptOrSuite: [''],
      place: [''],
      phone: ['']
    });
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((user) => {
        if (!user || this.user) return;
        this.user = user;
        this.timeZone = user.timeZone || this.timeZone;

        const now = moment().tz(this.timeZone);
        this.minDate = this.isEdit ? null : now.clone().startOf('day');
        this.tripForm.patchValue({
          company: user._id,
          preScheduleTime: now.clone(),
          scheduleTime: now.toISOString(),
          preAppointmentTime: now.clone().add(1, 'hours'),
          appointmentTime: now.clone().add(1, 'hours').toISOString()
        });

        if (this.isEdit) {
          this.getTripDetails();
        } else {
          this.isLoading = false;
        }
      });
  }

  hasSuggestedDetails = false;

  originalTripData: any = null;

  getTripDetails() {
    this.stagingService
      .getMemberLinkRequest(this.tripId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isLoading = false;
          if (!res) return;
          const originalData = this.normalizeOriginalTrip(res);
          this.originalTripData = originalData;
          const data = this.normalizeTrip(res);
          this.tripDetails = data;
          const preData = JSON.parse(JSON.stringify(data));
          delete preData.recurringDays;
          delete preData.addonServices;
          delete preData.tripLegs;

          const originParsed = preData.jobOriginAddress ? parseAddress(preData.jobOriginAddress) : null;
          const destinationParsed = preData.jobDestinationAddress ? parseAddress(preData.jobDestinationAddress) : null;

          this.tripForm.patchValue({
            ...preData,
            preScheduleTime: moment(preData.scheduleTime).tz(this.timeZone),
            preAppointmentTime: moment(preData.appointmentTime).tz(this.timeZone),
            payorId: preData.payorId || '',
            cityOrg: originParsed ? originParsed.city : '',
            stateOrg: originParsed ? originParsed.state : '',
            puStreet: originParsed ? originParsed.street : '',
            cityDest: destinationParsed ? destinationParsed.city : '',
            stateDest: destinationParsed ? destinationParsed.state : '',
            doStreet: destinationParsed ? destinationParsed.street : ''
          });

          this.setStandingOrderFromTrip(data);

          this.filterSpaceTypes(data.los, false);
          if (data.cooperate) {
            this.getMemberList(data.cooperate);
          }

          this.captureInitialSnapshot(originalData);
          this.computeChanges();
          if (this.hasSuggestedDetails || this.changedFields.length > 0) {
            this.isExtendedViewOpen = true;
          }
        },
        (err) => {
          this.isLoading = false;
          sweetAlert('Error', err, 'error', 'OK');
        }
      );
  }

  // Original baseline before suggested modifications
  normalizeOriginalTrip(raw) {
    const idOf = (v) => (v && typeof v === 'object' ? v._id : v);
    const trip = { ...raw };
    delete trip.suggested;
    delete trip.intakeSuggestedTime;
    trip.cooperate = idOf(raw.cooperate);
    trip.client = idOf(raw.client);
    trip.los = idOf(raw.los);
    trip.spaceType = idOf(raw.spaceType);
    trip.jobOriginAddress = raw.jobOriginAddress || raw.pickupAddress || (raw.pickupLocation && raw.pickupLocation.address) || '';
    trip.jobOriginLatitude = raw.jobOriginLatitude || (raw.pickupLocation && raw.pickupLocation.latitude) || 0;
    trip.jobOriginLongitude = raw.jobOriginLongitude || (raw.pickupLocation && raw.pickupLocation.longitude) || 0;
    trip.jobDestinationAddress = raw.jobDestinationAddress || raw.dropoffAddress || (raw.dropoffLocation && raw.dropoffLocation.address) || '';
    trip.jobDestinationLatitude = raw.jobDestinationLatitude || (raw.dropoffLocation && raw.dropoffLocation.latitude) || 0;
    trip.jobDestinationLongitude = raw.jobDestinationLongitude || (raw.dropoffLocation && raw.dropoffLocation.longitude) || 0;
    trip.scheduleTime = raw.scheduleTime || raw.pickupDateTime;
    trip.appointmentTime = raw.appointmentTime || raw.dropoffDateTime;
    trip.companyNote = raw.companyNote || '';
    trip.recurringDays = raw.recurringDays || [];
    trip.recurringEndDate = raw.recurringEndDate || raw.preRecurringEndDate || '';
    trip.isStandingOrder = raw.status === 'standing' || !!raw.isRecurring || !!raw.isStandingOrder || !!(raw.recurringDays && raw.recurringDays.length);
    trip.isRecurring = trip.isStandingOrder;
    return trip;
  }

  // MemberLink requests may return populated refs or alternate field names; map them to the form's shape
  normalizeTrip(raw) {
    const idOf = (v) => (v && typeof v === 'object' ? v._id : v);
    const trip = { ...raw };
    trip.cooperate = idOf(raw.cooperate);
    trip.client = idOf(raw.client);
    trip.los = idOf(raw.los);
    trip.spaceType = idOf(raw.spaceType);
    trip.jobOriginAddress = raw.jobOriginAddress || raw.pickupAddress || (raw.pickupLocation && raw.pickupLocation.address) || '';
    trip.jobDestinationAddress = raw.jobDestinationAddress || raw.dropoffAddress || (raw.dropoffLocation && raw.dropoffLocation.address) || '';
    trip.scheduleTime = raw.scheduleTime || raw.pickupDateTime;
    trip.appointmentTime = raw.appointmentTime || raw.dropoffDateTime;

    // Load suggested details if exist
    const suggested = raw.suggested;
    const hasSuggested = !!(suggested || raw.intakeSuggestedTime || raw.intakeStatus === 'suggested_plan');
    this.hasSuggestedDetails = hasSuggested;

    if (suggested) {
      if (suggested.scheduleTime || suggested.pickupDateTime) {
        trip.scheduleTime = suggested.scheduleTime || suggested.pickupDateTime;
      } else if (raw.intakeSuggestedTime) {
        trip.scheduleTime = raw.intakeSuggestedTime;
      }
      if (suggested.appointmentTime || suggested.dropoffDateTime) {
        trip.appointmentTime = suggested.appointmentTime || suggested.dropoffDateTime;
      }
      if (suggested.jobOriginAddress || suggested.pickupAddress) {
        trip.jobOriginAddress = suggested.jobOriginAddress || suggested.pickupAddress;
      }
      if (suggested.jobOriginLatitude !== undefined && suggested.jobOriginLatitude !== null) {
        trip.jobOriginLatitude = suggested.jobOriginLatitude;
      }
      if (suggested.jobOriginLongitude !== undefined && suggested.jobOriginLongitude !== null) {
        trip.jobOriginLongitude = suggested.jobOriginLongitude;
      }
      if (suggested.jobDestinationAddress || suggested.dropoffAddress) {
        trip.jobDestinationAddress = suggested.jobDestinationAddress || suggested.dropoffAddress;
      }
      if (suggested.jobDestinationLatitude !== undefined && suggested.jobDestinationLatitude !== null) {
        trip.jobDestinationLatitude = suggested.jobDestinationLatitude;
      }
      if (suggested.jobDestinationLongitude !== undefined && suggested.jobDestinationLongitude !== null) {
        trip.jobDestinationLongitude = suggested.jobDestinationLongitude;
      }
      if (suggested.los) {
        trip.los = idOf(suggested.los);
      }
      if (suggested.spaceType) {
        trip.spaceType = idOf(suggested.spaceType);
      }
      if (suggested.recurringDays && suggested.recurringDays.length) {
        trip.recurringDays = suggested.recurringDays;
        trip.isStandingOrder = true;
        trip.isRecurring = true;
        trip.status = 'standing';
      }
      if (suggested.recurringEndDate || suggested.preRecurringEndDate) {
        trip.recurringEndDate = suggested.recurringEndDate || suggested.preRecurringEndDate;
      }
      if (suggested.milage !== undefined && suggested.milage !== null) {
        trip.milage = suggested.milage;
      } else if (suggested.miles !== undefined && suggested.miles !== null) {
        trip.milage = suggested.miles;
      }
    } else if (raw.intakeSuggestedTime) {
      trip.scheduleTime = raw.intakeSuggestedTime;
    }

    return trip;
  }

  getMemberList(fundingSourceId) {
    this.memberService
      .getMembers({ accountStatus: 'active', cooperate: fundingSourceId })
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((data) => {
        this.memberList = data || [];
        if (this.isEdit && this.tripDetails) {
          this.selectedMember = this.memberList.find((m) => m._id === this.tripDetails.client);
          this.tripForm.patchValue({
            eligibilityStatus: this.selectedMember && this.selectedMember.eligibility ? this.selectedMember.eligibility.status : ''
          });
          if (this.initialLabels && !this.initialLabels.client && this.selectedMember) {
            this.initialLabels.client = this.selectedMember.displayName;
          }
          this.computeChanges();
        }
      });
  }

  onSelectFundingSource(fundingSourceId) {
    this.memberList = [];
    this.selectedMember = null;
    this.tripForm.patchValue({ client: '', cooperateDetails: { displayName: '', profileImageURL: '' } });

    const fundingSource = this.fundingSourceList.find((f) => f._id === fundingSourceId);
    if (fundingSource) {
      this.tripForm.patchValue({
        cooperateDetails: {
          displayName: fundingSource.displayName,
          profileImageURL: fundingSource.profileImageURL
        }
      });
      this.getMemberList(fundingSource._id);
    }
    this.computeChanges();
  }

  onSelectMember(memberId) {
    const member = this.memberList.find((m) => m._id === memberId);
    this.selectedMember = member;
    if (!member) {
      this.computeChanges();
      return;
    }

    let patch: any = {
      priorityClient: {
        ...member,
        preDob: member.dob ? moment(member.dob) : '',
        dob: member.dob || ''
      },
      isVipTrip: member.type === 'vip',
      eligibilityStatus: member.eligibility ? member.eligibility.status : '',
      originAddressAdditional: { phone: member.contactNumber }
    };

    // Default pickup to the member's home address on a new trip
    if (!this.isEdit && member.homeAddress && member.homeAddress.address) {
      const parsed = parseAddress(member.homeAddress.address);
      patch = {
        ...patch,
        jobOriginAddress: member.homeAddress.address,
        jobOriginLatitude: member.homeAddress.latitude,
        jobOriginLongitude: member.homeAddress.longitude,
        zipCodeOrg: member.homeAddress.zipCode,
        cityOrg: parsed ? parsed.city : '',
        stateOrg: parsed ? parsed.state : '',
        puStreet: parsed ? parsed.street : ''
      };
    }
    if (member.spaceType) {
      patch.spaceType = member.spaceType;
    }
    this.tripForm.patchValue(patch);

    if (member.los) {
      this.tripForm.patchValue({ los: member.los });
      this.filterSpaceTypes(member.los, false);
    }
    this.computeChanges();
  }

  onSelectLOS(losId) {
    this.filterSpaceTypes(losId, true);
    this.computeChanges();
  }

  filterSpaceTypes(losId, resetIfMissing: boolean) {
    this.spaceTypeList = losId ? this.spaceTypeMainList.filter((s) => s.los === losId) : [];
    const exists = this.spaceTypeList.some((s) => s._id === this.tripForm.value.spaceType);
    if (!exists && resetIfMissing) {
      this.tripForm.patchValue({ spaceType: '' });
    }
  }

  // --- Addresses -------------------------------------------------------------

  private addressPatch(place, prefix: 'Org' | 'Dest') {
    const details = getAddressDetails(place) || {};
    const isOrigin = prefix === 'Org';
    return {
      [isOrigin ? 'jobOriginAddress' : 'jobDestinationAddress']: place.formatted_address,
      [isOrigin ? 'jobOriginLatitude' : 'jobDestinationLatitude']: place.geometry.location.lat(),
      [isOrigin ? 'jobOriginLongitude' : 'jobDestinationLongitude']: place.geometry.location.lng(),
      [`zipCode${prefix}`]: details.zipCode,
      [`city${prefix}`]: details.city,
      [`state${prefix}`]: details.state,
      [isOrigin ? 'puStreet' : 'doStreet']: details.street
    };
  }

  setOriginAddress(place, leg?: FormGroup) {
    if (!place || !place.geometry) return;
    (leg || this.tripForm).patchValue(this.addressPatch(place, 'Org'));
    this.computeChanges();
  }

  setDestinationAddress(place, leg?: FormGroup) {
    if (!place || !place.geometry) return;
    (leg || this.tripForm).patchValue(this.addressPatch(place, 'Dest'));
    this.computeChanges();
  }

  // Typing after picking a suggestion invalidates the coordinates
  onOriginTyped(leg?: FormGroup) {
    (leg || this.tripForm).patchValue({ jobOriginLatitude: 0, jobOriginLongitude: 0, zipCodeOrg: '' });
    this.computeChanges();
  }

  onDestinationTyped(leg?: FormGroup) {
    (leg || this.tripForm).patchValue({ jobDestinationLatitude: 0, jobDestinationLongitude: 0, zipCodeDest: '' });
    this.computeChanges();
  }

  clearOrigin(leg?: FormGroup) {
    (leg || this.tripForm).patchValue({ jobOriginAddress: '' });
    this.onOriginTyped(leg);
    this.computeChanges();
  }

  clearDestination(leg?: FormGroup) {
    (leg || this.tripForm).patchValue({ jobDestinationAddress: '' });
    this.onDestinationTyped(leg);
    this.computeChanges();
  }

  hasCoords(group: FormGroup, type: 'origin' | 'destination'): boolean {
    const v = group.value;
    return type === 'origin'
      ? !!(v.jobOriginLatitude && v.jobOriginLongitude)
      : !!(v.jobDestinationLatitude && v.jobDestinationLongitude);
  }

  // --- Times -----------------------------------------------------------------

  computeAppointmentFromPickup(pickupMoment: any, currentAptValue: any) {
    const endOfDay = pickupMoment.clone().set({ hour: 23, minute: 59, second: 0, millisecond: 0 });

    // Normal minimum difference is 1 hour in future
    let minApt = pickupMoment.clone().add(1, 'hours');
    if (minApt.isAfter(endOfDay)) {
      minApt = endOfDay.clone();
    }

    if (!currentAptValue) {
      return minApt;
    }

    const aptMoment = moment(currentAptValue).tz(this.timeZone, true);
    if (!aptMoment.isValid()) {
      return minApt;
    }

    // Preserve the time on the new pickup date
    let candidateApt = pickupMoment.clone().set({
      hour: aptMoment.hour(),
      minute: aptMoment.minute(),
      second: aptMoment.second(),
      millisecond: 0
    });

    // Appointment must be in future (minimum 1 hr diff) and capped at 23:59
    if (candidateApt.isBefore(minApt)) {
      candidateApt = minApt.clone();
    } else if (candidateApt.isAfter(endOfDay)) {
      candidateApt = endOfDay.clone();
    }

    return candidateApt;
  }

  getMinTime(group: FormGroup, kind: string) {
    if (kind === 'dropoff' && group) {
      const pickupCtrl = group.get('preScheduleTime');
      const pickupVal = pickupCtrl ? pickupCtrl.value : null;
      if (pickupVal) {
        return pickupVal;
      }
    }
    return this.minDate;
  }

  getMaxTime(group: FormGroup, kind: string) {
    if (kind === 'dropoff' && group) {
      const pickupCtrl = group.get('preScheduleTime');
      const pickupVal = pickupCtrl ? pickupCtrl.value : null;
      if (pickupVal) {
        return moment(pickupVal).tz(this.timeZone, true).clone().set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
      }
    }
    return null;
  }

  setPickupTime(event, leg?: FormGroup) {
    if (!event) return;
    const targetGroup = leg || this.tripForm;
    const newPickup = moment(event).tz(this.timeZone, true);
    const patchObj: any = {
      scheduleTime: newPickup.toISOString()
    };

    const currentAptControl = targetGroup.get('preAppointmentTime');
    const currentApt = currentAptControl ? currentAptControl.value : null;
    const newApt = this.computeAppointmentFromPickup(newPickup, currentApt);

    patchObj.preAppointmentTime = newApt;
    patchObj.appointmentTime = newApt.toISOString();

    targetGroup.patchValue(patchObj);

    if (!leg && this.returnLeg) {
      const returnLeg = this.returnLeg;
      const retPickupControl = returnLeg.get('preScheduleTime');
      const retPickup = retPickupControl ? retPickupControl.value : null;
      if (retPickup) {
        const retPickupMoment = moment(retPickup).tz(this.timeZone, true);
        const newRetPickup = newPickup.clone().set({
          hour: retPickupMoment.hour(),
          minute: retPickupMoment.minute(),
          second: retPickupMoment.second(),
          millisecond: retPickupMoment.millisecond()
        });
        const retAptControl = returnLeg.get('preAppointmentTime');
        const retApt = retAptControl ? retAptControl.value : null;
        const newRetApt = this.computeAppointmentFromPickup(newRetPickup, retApt);

        returnLeg.patchValue({
          preScheduleTime: newRetPickup,
          scheduleTime: newRetPickup.toISOString(),
          preAppointmentTime: newRetApt,
          appointmentTime: newRetApt.toISOString()
        });
      }
    }

    this.computeChanges();
  }

  setDropoffTime(event, leg?: FormGroup) {
    if (!event) return;
    const targetGroup = leg || this.tripForm;
    const dropoffMoment = moment(event).tz(this.timeZone, true);

    const pickupCtrl = targetGroup.get('preScheduleTime');
    const pickupVal = pickupCtrl ? pickupCtrl.value : null;
    if (pickupVal) {
      const pickupMoment = moment(pickupVal).tz(this.timeZone, true);
      if (dropoffMoment.isBefore(pickupMoment)) {
        const endOfDay = pickupMoment.clone().set({ hour: 23, minute: 59, second: 0, millisecond: 0 });
        let correctedApt = pickupMoment.clone().add(1, 'hours');
        if (correctedApt.isAfter(endOfDay)) {
          correctedApt = endOfDay.clone();
        }
        targetGroup.patchValue({
          preAppointmentTime: correctedApt,
          appointmentTime: correctedApt.toISOString()
        });
        this.computeChanges();
        return;
      }
    }

    targetGroup.patchValue({ appointmentTime: dropoffMoment.toISOString() });
    this.computeChanges();
  }

  // --- Return trip -----------------------------------------------------------

  toggleReturnTrip(enabled: boolean) {
    this.legs.clear();
    if (!enabled) return;

    const v = this.tripForm.value;
    const base = moment(v.preAppointmentTime || moment().tz(this.timeZone)).clone().add(1, 'hours');
    this.legs.push(this.fb.group({
      jobOriginAddress: [v.jobDestinationAddress, Validators.required],
      jobOriginLatitude: [v.jobDestinationLatitude, Validators.required],
      jobOriginLongitude: [v.jobDestinationLongitude, Validators.required],
      zipCodeOrg: [v.zipCodeDest],
      cityOrg: [v.cityDest],
      stateOrg: [v.stateDest],
      puStreet: [v.doStreet || ''],

      jobDestinationAddress: [v.jobOriginAddress, Validators.required],
      jobDestinationLatitude: [v.jobOriginLatitude, Validators.required],
      jobDestinationLongitude: [v.jobOriginLongitude, Validators.required],
      zipCodeDest: [v.zipCodeOrg],
      cityDest: [v.cityOrg],
      stateDest: [v.stateOrg],
      doStreet: [v.puStreet || ''],

      preScheduleTime: [base.clone(), Validators.required],
      scheduleTime: [base.toISOString(), Validators.required],
      preAppointmentTime: [base.clone().add(1, 'hours'), Validators.required],
      appointmentTime: [base.clone().add(1, 'hours').toISOString(), Validators.required],
      companyNote: [''],
      isWillCall: [false],
      originAddressAdditional: this.buildAddressAdditional(),
      destinationAddressAdditional: this.buildAddressAdditional(),
      appointmentDirection: ['']
    }));
  }

  onReturnWillCallChange(event) {
    this.setReturnWillCall(!!(event && event.target && event.target.checked));
  }

  setReturnWillCall(checked: boolean) {
    const leg = this.returnLeg;
    if (!leg) return;
    const pickup = leg.controls.preScheduleTime;
    if (checked) {
      const time = moment(pickup.value).clone().set({ hour: 23, minute: 59 });
      leg.patchValue({
        isWillCall: true,
        preScheduleTime: time.clone(),
        scheduleTime: moment(time).tz(this.timeZone, true).toISOString(),
        preAppointmentTime: time.clone(),
        appointmentTime: moment(time).tz(this.timeZone, true).toISOString()
      });
      pickup.disable();
    } else {
      leg.patchValue({ isWillCall: false });
      pickup.enable();
    }
  }

  // --- Standing order ---------------------------------------------------------

  isOriginalStandingOrder = false;

  setStandingOrderFromTrip(trip, checkSuggested: boolean = true) {
    const suggested = checkSuggested ? trip.suggested : null;
    const recurringDays = (suggested && suggested.recurringDays && suggested.recurringDays.length)
      ? suggested.recurringDays
      : (trip.recurringDays || []);
    const recurringEndDate = (suggested && (suggested.recurringEndDate || suggested.preRecurringEndDate))
      ? (suggested.recurringEndDate || suggested.preRecurringEndDate)
      : (trip.recurringEndDate || trip.preRecurringEndDate);

    this.isStandingOrder = trip.status === 'standing' || !!trip.isRecurring || !!trip.isStandingOrder || !!(recurringDays && recurringDays.length);
    this.isOriginalStandingOrder = this.isStandingOrder;
    this.recurringDays = (recurringDays || [])
      .filter((d: any) => d && d.checked !== false && d.day !== undefined && d.day !== null)
      .map((d: any) => (typeof d === 'number' ? d : Number(d.day)))
      .sort();
    this.recurringEndDate = recurringEndDate ? moment(recurringEndDate).tz(this.timeZone) : null;
  }

  toggleStandingOrder() {
    if (this.isEdit && this.isOriginalStandingOrder) {
      return;
    }
    this.isStandingOrder = !this.isStandingOrder;
    // Start with the pickup's own weekday selected, as the most likely repeat
    if (this.isStandingOrder && !this.recurringDays.length && this.tripForm.value.preScheduleTime) {
      this.recurringDays = [moment(this.tripForm.value.preScheduleTime).day()];
    }
  }

  toggleRecurringDay(day: number) {
    this.recurringDays = this.recurringDays.includes(day)
      ? this.recurringDays.filter((d) => d !== day)
      : [...this.recurringDays, day].sort();
    this.computeChanges();
  }

  get standingOrderError(): string {
    if (!this.isStandingOrder) return '';
    if (!this.recurringDays.length) return 'Select at least one day';
    if (!this.recurringEndDate) return 'End date is required';
    const pickup = this.tripForm.value.preScheduleTime;
    if (this.recurringEndDate && pickup
      && moment(this.recurringEndDate).format('YYYY-MM-DD') < moment(pickup).format('YYYY-MM-DD')) {
      return "End date can't be before the pickup date";
    }
    return '';
  }

  // Mirrors buildRecurringFields in transmedik-membercare/src/services/tripPayload.js
  private applyRecurringFields(payload: any) {
    if (!this.isStandingOrder) {
      payload.preRecurringEndDate = null;
      payload.recurringEndDate = '';
      payload.isStandingOrder = false;
      payload.isRecurring = false;
      delete payload.recurringDays;
      delete payload.status;
      delete payload.jobStatus;
      return;
    }

    const endDateIso = this.recurringEndDate
      ? moment.tz(`${moment(this.recurringEndDate).format('YYYY-MM-DD')} 23:59:00`, 'YYYY-MM-DD HH:mm:ss', this.timeZone).toISOString()
      : null;
    payload.preRecurringEndDate = endDateIso;
    payload.recurringEndDate = endDateIso || '';
    payload.isStandingOrder = true;
    payload.isRecurring = true;
    payload.recurringDays = this.recurringDays.map((day) => ({
      label: this.weekDays[day].label,
      day,
      checked: true
    }));
    payload.status = 'standing';
    payload.jobStatus = 'standing';
  }

  // --- Submit ----------------------------------------------------------------

  // Addresses must be picked from Google suggestions so they have coordinates
  addressesHaveCoords(): boolean {
    const groups: FormGroup[] = [this.tripForm, ...(this.returnLeg ? [this.returnLeg] : [])];
    return groups.every((g) => this.hasCoords(g, 'origin') && this.hasCoords(g, 'destination'));
  }

  async save() {
    this.submitted = true;
    if (this.tripForm.invalid || !this.addressesHaveCoords() || this.standingOrderError || this.isSaving) {
      this.toastr.error('Please fill in all required fields', 'Missing information');
      return;
    }

    const formVal = this.tripForm.value;
    if (formVal.scheduleTime && formVal.appointmentTime) {
      const sched = moment(formVal.scheduleTime);
      const apt = moment(formVal.appointmentTime);
      if (apt.isBefore(sched)) {
        this.toastr.error('Appointment time cannot be before pickup time', 'Invalid Time');
        return;
      }
    }
    if (this.returnLeg) {
      const retVal = this.returnLeg.value;
      if (retVal.scheduleTime && retVal.appointmentTime) {
        const retSched = moment(retVal.scheduleTime);
        const retApt = moment(retVal.appointmentTime);
        if (retApt.isBefore(retSched)) {
          this.toastr.error('Return appointment time cannot be before return pickup time', 'Invalid Time');
          return;
        }
      }
    }

    if (this.isEdit) {
      // Same pre-save checks as the full trip edit page
      const latest: any = await this.stagingService.getMemberLinkRequest(this.tripId).toPromise().catch(() => null);
      if (latest && latest.status === 'inprogress') {
        sweetAlert('Alert', 'Inprogress trip cannot be edited', 'warning', 'Okay');
        return;
      }
      if (this.user && this.user.showAddressValidationAlert && this.tripDetails
        && !(this.tripDetails.status === 'standing' || this.tripDetails.so)
        && this.isAddressesModified()) {
        const isProceed = await this.confirmAddressUpdate();
        if (!isProceed) return;
      }
    }

    if (this.tripForm.value.eligibilityStatus === 'ineligible') {
      const confirm = await sweetAlert('Warning', 'Member is not eligible for this ride. Do you want to proceed?', 'warning', 'Yes', 'No');
      if (!confirm.value) return;
    }

    // getRawValue keeps disabled will-call times in the payload
    const payload: any = this.tripForm.getRawValue();
    if (this.tripForm.get('isUpdateSameDayLegs').disabled) delete payload.isUpdateSameDayLegs;
    if (this.tripForm.get('isUpdateFutureLegs').disabled) delete payload.isUpdateFutureLegs;
    payload.attendees = null;
    payload.payorId = payload.payorId || null;
    payload.actualAppointmentTime = '';
    payload.isEnableExpectedAppointment = false;
    const currentIntakeStatus = (this.originalTripData && this.originalTripData.intakeStatus)
      || (this.tripDetails && this.tripDetails.intakeStatus)
      || '';

    if (this.isEdit && currentIntakeStatus === 'unconfirmed') {
      payload.intakeStatus = 'unconfirmed';
      if (this.originalTripData && (this.originalTripData.intakeRejectionReason || this.originalTripData.intakeRejectReason)) {
        payload.intakeRejectionReason = this.originalTripData.intakeRejectionReason || this.originalTripData.intakeRejectReason;
      }
    } else {
      payload.intakeStatus = 'trip_intake';
    }
    payload.suggested = null;
    payload.intakeSuggestedTime = null;
    this.applyRecurringFields(payload);
    if (!payload.milage) delete payload.milage;

    const nullModifier = (item) => {
      if (!item.originAddressAdditional.modifier) item.originAddressAdditional.modifier = null;
      if (!item.destinationAddressAdditional.modifier) item.destinationAddressAdditional.modifier = null;
    };
    nullModifier(payload);
    (payload.tripLegs || []).forEach(nullModifier);

    this.isSaving = true;
    const request$ = this.isEdit
      ? this.stagingService.updateMemberLinkRequest(this.tripId, payload)
      : this.stagingService.createMemberLinkRequest(payload);

    request$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isSaving = false;
          this.toastr.success(this.isEdit ? 'Trip updated successfully' : 'Trip created successfully', 'Success');
          this.onSaved.emit({ trip: data, isEdit: this.isEdit });
        },
        (err) => {
          this.isSaving = false;
          sweetAlert('Error', err, 'error', 'OK');
        }
      );
  }

  clearSuggestion() {
    if (!this.originalTripData) return;
    const orig = this.originalTripData;
    this.tripDetails = orig;
    const preData = JSON.parse(JSON.stringify(orig));
    delete preData.recurringDays;
    delete preData.addonServices;
    delete preData.tripLegs;

    const originParsed = preData.jobOriginAddress ? parseAddress(preData.jobOriginAddress) : null;
    const destinationParsed = preData.jobDestinationAddress ? parseAddress(preData.jobDestinationAddress) : null;

    this.filterSpaceTypes(orig.los, false);

    this.tripForm.patchValue({
      ...preData,
      los: orig.los,
      spaceType: orig.spaceType,
      jobOriginAddress: orig.jobOriginAddress || '',
      jobOriginLatitude: orig.jobOriginLatitude || 0,
      jobOriginLongitude: orig.jobOriginLongitude || 0,
      jobDestinationAddress: orig.jobDestinationAddress || '',
      jobDestinationLatitude: orig.jobDestinationLatitude || 0,
      jobDestinationLongitude: orig.jobDestinationLongitude || 0,
      scheduleTime: orig.scheduleTime,
      appointmentTime: orig.appointmentTime,
      preScheduleTime: moment(orig.scheduleTime).tz(this.timeZone),
      preAppointmentTime: moment(orig.appointmentTime).tz(this.timeZone),
      payorId: preData.payorId || '',
      cityOrg: originParsed ? originParsed.city : '',
      stateOrg: originParsed ? originParsed.state : '',
      puStreet: originParsed ? originParsed.street : '',
      cityDest: destinationParsed ? destinationParsed.city : '',
      stateDest: destinationParsed ? destinationParsed.state : '',
      doStreet: destinationParsed ? destinationParsed.street : '',
      companyNote: orig.companyNote || ''
    });

    this.tripForm.patchValue({ spaceType: orig.spaceType });

    this.setStandingOrderFromTrip(orig, false);
    if (orig.cooperate) {
      this.getMemberList(orig.cooperate);
    }

    this.hasSuggestedDetails = false;
    this.isExtendedViewOpen = false;
    this.captureInitialSnapshot(orig);
    this.changedFields = [];
    this.tripForm.markAsPristine();
    this.tripForm.markAsUntouched();

    this.toastr.info('Suggestion cleared. Original trip details restored.', 'Suggestion Cleared');
  }

  buildSuggestedObject(): any {
    const v = this.tripForm.getRawValue();
    const suggested: any = {};

    for (const change of this.changedFields) {
      switch (change.key) {
        case 'scheduleTime':
          suggested.scheduleTime = v.scheduleTime;
          break;
        case 'appointmentTime':
          suggested.appointmentTime = v.appointmentTime;
          break;
        case 'jobOriginAddress':
          suggested.jobOriginAddress = v.jobOriginAddress;
          suggested.jobOriginLatitude = v.jobOriginLatitude;
          suggested.jobOriginLongitude = v.jobOriginLongitude;
          if (v.zipCodeOrg) suggested.zipCodeOrg = v.zipCodeOrg;
          if (v.cityOrg) suggested.cityOrg = v.cityOrg;
          if (v.stateOrg) suggested.stateOrg = v.stateOrg;
          if (v.puStreet) suggested.puStreet = v.puStreet;
          break;
        case 'jobDestinationAddress':
          suggested.jobDestinationAddress = v.jobDestinationAddress;
          suggested.jobDestinationLatitude = v.jobDestinationLatitude;
          suggested.jobDestinationLongitude = v.jobDestinationLongitude;
          if (v.zipCodeDest) suggested.zipCodeDest = v.zipCodeDest;
          if (v.cityDest) suggested.cityDest = v.cityDest;
          if (v.stateDest) suggested.stateDest = v.stateDest;
          if (v.doStreet) suggested.doStreet = v.doStreet;
          break;
        case 'cooperate':
          suggested.cooperate = v.cooperate;
          if (v.cooperateDetails) suggested.cooperateDetails = v.cooperateDetails;
          break;
        case 'client':
          suggested.client = v.client;
          break;
        case 'los':
          suggested.los = v.los;
          break;
        case 'spaceType':
          suggested.spaceType = v.spaceType;
          break;
        case 'displayName':
          suggested.displayName = (v.priorityClient && v.priorityClient.displayName) || '';
          if (!suggested.priorityClient) {
            suggested.priorityClient = { ...((this.tripDetails && this.tripDetails.priorityClient) || {}) };
          }
          suggested.priorityClient.displayName = suggested.displayName;
          break;
        case 'contactNumber':
          suggested.contactNumber = (v.priorityClient && v.priorityClient.contactNumber) || '';
          if (!suggested.priorityClient) {
            suggested.priorityClient = { ...((this.tripDetails && this.tripDetails.priorityClient) || {}) };
          }
          suggested.priorityClient.contactNumber = suggested.contactNumber;
          break;
        case 'recurringDays':
          suggested.recurringDays = this.recurringDays.map((day) => ({
            label: this.weekDays[day].label,
            day,
            checked: true
          }));
          suggested.isStandingOrder = true;
          suggested.isRecurring = true;
          suggested.status = 'standing';
          suggested.jobStatus = 'standing';
          break;
        case 'recurringEndDate':
          const endDateIso = this.recurringEndDate
            ? moment.tz(`${moment(this.recurringEndDate).format('YYYY-MM-DD')} 23:59:00`, 'YYYY-MM-DD HH:mm:ss', this.timeZone).toISOString()
            : null;
          suggested.preRecurringEndDate = endDateIso;
          suggested.recurringEndDate = endDateIso || '';
          break;
      }
    }

    return suggested;
  }

  async saveSuggestedPlan() {
    this.submitted = true;
    if (this.tripForm.invalid || !this.addressesHaveCoords() || this.standingOrderError || this.isSaving) {
      this.toastr.error(this.standingOrderError || 'Please fill in all required fields', 'Missing information');
      return;
    }

    const formVal = this.tripForm.value;
    if (formVal.scheduleTime && formVal.appointmentTime) {
      const sched = moment(formVal.scheduleTime);
      const apt = moment(formVal.appointmentTime);
      if (apt.isBefore(sched)) {
        this.toastr.error('Appointment time cannot be before pickup time', 'Invalid Time');
        return;
      }
    }

    if (!this.changedFields.length) {
      this.toastr.warning('No changes detected to suggest.', 'No Changes');
      return;
    }

    const tripId = this.tripId || (this.tripDetails && (this.tripDetails._id || this.tripDetails.id));
    if (!tripId) {
      this.toastr.error('Trip ID is missing.', 'Error');
      return;
    }

    if (this.tripDetails && this.tripDetails.status === 'inprogress') {
      sweetAlert('Alert', 'Inprogress trip cannot be updated', 'warning', 'Okay');
      return;
    }

    const suggested = this.buildSuggestedObject();
    this.isSaving = true;

    this.stagingService
      .suggestPlan(tripId, suggested)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isSaving = false;
          this.toastr.success('Suggested plan saved successfully', 'Success');
          this.onSaved.emit({ trip: data, isEdit: this.isEdit, action: 'suggested_plan' });
        },
        (err) => {
          this.isSaving = false;
          const msg = (err && (err.message || err.error)) || err || 'Failed to save suggested plan';
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  isAddressesModified(): boolean {
    return this.tripDetails.jobOriginAddress !== this.tripForm.value.jobOriginAddress
      || this.tripDetails.jobDestinationAddress !== this.tripForm.value.jobDestinationAddress;
  }

  async confirmAddressUpdate(): Promise<boolean> {
    const confirm = await sweetAlertWithTwoCheckboxes(
      'Address Validation Alert!',
      'You want to apply address changes to associated trips?',
      'warning',
      'Proceed',
      'No'
    );
    if (!confirm.value) return false;

    const { isUpdateSameDayLegs, isUpdateFutureLegs } = confirm.value;
    if (isUpdateSameDayLegs) {
      const control = this.tripForm.get('isUpdateSameDayLegs');
      control.enable();
      control.patchValue(true);
    }
    if (isUpdateFutureLegs) {
      const control = this.tripForm.get('isUpdateFutureLegs');
      control.enable();
      control.patchValue(true);
    }
    return true;
  }

  // --- Change Tracking for Extended View --------------------------------------

  getFundingSourceNameById(id: string): string {
    if (!id) return '';
    const item = this.fundingSourceList.find((f) => f._id === id);
    if (item) return item.displayName || item.name || '';
    if (this.tripDetails && this.tripDetails.cooperate) {
      if (typeof this.tripDetails.cooperate === 'object') {
        return this.tripDetails.cooperate.displayName || '';
      }
    }
    return '';
  }

  getMemberNameById(id: string): string {
    if (!id) return '';
    const item = this.memberList.find((m) => m._id === id);
    if (item) {
      return item.displayName + (item.contactNumber ? ` - ${item.contactNumber}` : '');
    }
    if (this.selectedMember && this.selectedMember._id === id) {
      return this.selectedMember.displayName;
    }
    if (this.tripDetails) {
      if (this.tripDetails.priorityClient && this.tripDetails.priorityClient.displayName) {
        return this.tripDetails.priorityClient.displayName;
      }
      if (this.tripDetails.client && typeof this.tripDetails.client === 'object') {
        return this.tripDetails.client.displayName || '';
      }
    }
    return '';
  }

  getLosNameById(id: string): string {
    if (!id) return '';
    const item = this.levelOfServiceList.find((l) => l._id === id);
    if (item) return item.title || '';
    if (this.tripDetails && this.tripDetails.los && typeof this.tripDetails.los === 'object') {
      return this.tripDetails.los.title || '';
    }
    return '';
  }

  getSpaceTypeNameById(id: string): string {
    if (!id) return '';
    const item = this.spaceTypeMainList.find((s) => s._id === id);
    if (item) return item.title || '';
    if (this.tripDetails && this.tripDetails.spaceType && typeof this.tripDetails.spaceType === 'object') {
      return this.tripDetails.spaceType.title || '';
    }
    return '';
  }

  formatDateTime(isoDate: any): string {
    if (!isoDate) return 'Not set';
    const m = moment(isoDate).tz(this.timeZone);
    return m.isValid() ? m.format('MM/DD/YYYY HHmm') : String(isoDate);
  }

  captureInitialSnapshot(raw?: any) {
    const rawData = raw || this.tripDetails || {};
    const idOf = (v) => (v && typeof v === 'object' ? v._id : v);

    const cooperateId = idOf(rawData.cooperate) || '';
    const clientId = idOf(rawData.client) || '';
    const losId = idOf(rawData.los) || '';
    const spaceTypeId = idOf(rawData.spaceType) || '';

    const clientDisplayName = (rawData.priorityClient && rawData.priorityClient.displayName)
      || (rawData.client && typeof rawData.client === 'object' ? rawData.client.displayName : '')
      || '';
    const clientContact = (rawData.priorityClient && rawData.priorityClient.contactNumber)
      || (rawData.client && typeof rawData.client === 'object' ? rawData.client.contactNumber : '')
      || '';

    const originalRecurringDays = (rawData.recurringDays || [])
      .filter((d: any) => d && d.checked !== false && d.day !== undefined && d.day !== null)
      .map((d: any) => (typeof d === 'number' ? d : Number(d.day)))
      .sort();

    const originalRecurringEndDate = rawData.recurringEndDate
      ? moment(rawData.recurringEndDate).toISOString()
      : (rawData.preRecurringEndDate ? moment(rawData.preRecurringEndDate).toISOString() : '');

    this.initialSnapshot = {
      cooperate: cooperateId,
      client: clientId,
      los: losId,
      spaceType: spaceTypeId,
      displayName: clientDisplayName,
      contactNumber: clientContact,
      jobOriginAddress: rawData.jobOriginAddress || rawData.pickupAddress || (rawData.pickupLocation && rawData.pickupLocation.address) || '',
      jobOriginLatitude: rawData.jobOriginLatitude || 0,
      jobOriginLongitude: rawData.jobOriginLongitude || 0,
      zipCodeOrg: rawData.zipCodeOrg || '',
      cityOrg: rawData.cityOrg || '',
      stateOrg: rawData.stateOrg || '',
      puStreet: rawData.puStreet || '',
      jobDestinationAddress: rawData.jobDestinationAddress || rawData.dropoffAddress || (rawData.dropoffLocation && rawData.dropoffLocation.address) || '',
      jobDestinationLatitude: rawData.jobDestinationLatitude || 0,
      jobDestinationLongitude: rawData.jobDestinationLongitude || 0,
      zipCodeDest: rawData.zipCodeDest || '',
      cityDest: rawData.cityDest || '',
      stateDest: rawData.stateDest || '',
      doStreet: rawData.doStreet || '',
      scheduleTime: rawData.scheduleTime || rawData.pickupDateTime || '',
      appointmentTime: rawData.appointmentTime || rawData.dropoffDateTime || '',
      companyNote: rawData.companyNote || '',
      recurringDays: originalRecurringDays,
      recurringEndDate: originalRecurringEndDate
    };

    this.initialLabels = {
      cooperate: (rawData.cooperate && typeof rawData.cooperate === 'object' ? rawData.cooperate.displayName : '')
        || this.getFundingSourceNameById(cooperateId),
      client: clientDisplayName
        || this.getMemberNameById(clientId),
      los: (rawData.los && typeof rawData.los === 'object' ? (rawData.los.title || rawData.los.name) : '')
        || this.getLosNameById(losId),
      spaceType: (rawData.spaceType && typeof rawData.spaceType === 'object' ? (rawData.spaceType.title || rawData.spaceType.name) : '')
        || this.getSpaceTypeNameById(spaceTypeId)
    };

    this.changedFields = [];
  }

  onRecurringEndDateChange() {
    this.computeChanges();
  }

  clearRecurringEndDate() {
    this.recurringEndDate = null;
    this.computeChanges();
  }

  computeChanges() {
    if (!this.initialSnapshot || !this.isEdit) {
      this.changedFields = [];
      return;
    }

    const curr = this.tripForm.getRawValue();
    const changes: Array<{ key: string; label: string; from: string; to: string }> = [];

    // Funding Source
    if ((this.initialSnapshot.cooperate || '') !== (curr.cooperate || '')) {
      changes.push({
        key: 'cooperate',
        label: 'Funding Source',
        from: this.initialLabels.cooperate || this.getFundingSourceNameById(this.initialSnapshot.cooperate) || 'Not set',
        to: this.getFundingSourceNameById(curr.cooperate) || 'Not set'
      });
    }

    // Member
    if ((this.initialSnapshot.client || '') !== (curr.client || '')) {
      changes.push({
        key: 'client',
        label: 'Member',
        from: this.initialLabels.client || this.getMemberNameById(this.initialSnapshot.client) || 'Not set',
        to: this.getMemberNameById(curr.client) || 'Not set'
      });
    }

    // Level of Service
    if ((this.initialSnapshot.los || '') !== (curr.los || '')) {
      changes.push({
        key: 'los',
        label: 'Level of Service',
        from: this.initialLabels.los || this.getLosNameById(this.initialSnapshot.los) || 'Not set',
        to: this.getLosNameById(curr.los) || 'Not set'
      });
    }

    // Space Type
    if ((this.initialSnapshot.spaceType || '') !== (curr.spaceType || '')) {
      changes.push({
        key: 'spaceType',
        label: 'Space Type',
        from: this.initialLabels.spaceType || this.getSpaceTypeNameById(this.initialSnapshot.spaceType) || 'Not set',
        to: this.getSpaceTypeNameById(curr.spaceType) || 'Not set'
      });
    }

    // Display Name
    const initDisp = (this.initialSnapshot.displayName || '').trim();
    const currDisp = ((curr.priorityClient && curr.priorityClient.displayName) || '').trim();
    if (initDisp !== currDisp) {
      changes.push({
        key: 'displayName',
        label: 'Display Name',
        from: initDisp || 'Not set',
        to: currDisp || 'Not set'
      });
    }

    // Mobile Number
    const initPhone = (this.initialSnapshot.contactNumber || '').trim();
    const currPhone = ((curr.priorityClient && curr.priorityClient.contactNumber) || '').trim();
    if (initPhone !== currPhone) {
      changes.push({
        key: 'contactNumber',
        label: 'Mobile Number',
        from: initPhone || 'Not set',
        to: currPhone || 'Not set'
      });
    }

    // Pickup Address
    const initOrigin = (this.initialSnapshot.jobOriginAddress || '').trim();
    const currOrigin = (curr.jobOriginAddress || '').trim();
    if (initOrigin !== currOrigin) {
      changes.push({
        key: 'jobOriginAddress',
        label: 'Pickup Address',
        from: initOrigin || 'Not set',
        to: currOrigin || 'Not set'
      });
    }

    // Pickup Time
    const initPickup = this.initialSnapshot.scheduleTime
      ? moment(this.initialSnapshot.scheduleTime).tz(this.timeZone)
      : null;
    const currPickup = curr.scheduleTime
      ? moment(curr.scheduleTime).tz(this.timeZone)
      : null;
    const initPickupStr = initPickup && initPickup.isValid() ? initPickup.format('MM/DD/YYYY HHmm') : '';
    const currPickupStr = currPickup && currPickup.isValid() ? currPickup.format('MM/DD/YYYY HHmm') : '';
    if (initPickupStr && currPickupStr && initPickupStr !== currPickupStr) {
      changes.push({
        key: 'scheduleTime',
        label: 'Pickup Date & Time',
        from: this.formatDateTime(this.initialSnapshot.scheduleTime),
        to: this.formatDateTime(curr.scheduleTime)
      });
    }

    // Dropoff Address
    const initDest = (this.initialSnapshot.jobDestinationAddress || '').trim();
    const currDest = (curr.jobDestinationAddress || '').trim();
    if (initDest !== currDest) {
      changes.push({
        key: 'jobDestinationAddress',
        label: 'Dropoff Address',
        from: initDest || 'Not set',
        to: currDest || 'Not set'
      });
    }

    // Dropoff Time
    const initDrop = this.initialSnapshot.appointmentTime
      ? moment(this.initialSnapshot.appointmentTime).tz(this.timeZone)
      : null;
    const currDrop = curr.appointmentTime
      ? moment(curr.appointmentTime).tz(this.timeZone)
      : null;
    const initDropStr = initDrop && initDrop.isValid() ? initDrop.format('MM/DD/YYYY HHmm') : '';
    const currDropStr = currDrop && currDrop.isValid() ? currDrop.format('MM/DD/YYYY HHmm') : '';
    if (initDropStr && currDropStr && initDropStr !== currDropStr) {
      changes.push({
        key: 'appointmentTime',
        label: 'Dropoff Date & Time',
        from: this.formatDateTime(this.initialSnapshot.appointmentTime),
        to: this.formatDateTime(curr.appointmentTime)
      });
    }

    // Standing Order - Recurring Days
    if (this.isStandingOrder) {
      const initDays = (this.initialSnapshot.recurringDays || [])
        .map((d: any) => (typeof d === 'number' ? d : Number(d.day)))
        .filter((d: any) => !isNaN(d))
        .sort();
      const currDays = [...this.recurringDays].sort();
      const initDaysStr = initDays.map((d: number) => (this.weekDays[d] ? this.weekDays[d].label : d)).join(', ');
      const currDaysStr = currDays.map((d: number) => (this.weekDays[d] ? this.weekDays[d].label : d)).join(', ');
      if (initDays.join(',') !== currDays.join(',')) {
        changes.push({
          key: 'recurringDays',
          label: 'Repeat On Days',
          from: initDaysStr || 'None',
          to: currDaysStr || 'None'
        });
      }

      // Standing Order - End Date
      const initEnd = this.initialSnapshot.recurringEndDate;
      const initEndStr = initEnd ? moment(initEnd).tz(this.timeZone).format('MM/DD/YYYY') : '';
      const currEndStr = this.recurringEndDate ? moment(this.recurringEndDate).tz(this.timeZone).format('MM/DD/YYYY') : '';
      if (initEndStr !== currEndStr) {
        changes.push({
          key: 'recurringEndDate',
          label: 'Standing End Date',
          from: initEndStr || 'No end date',
          to: currEndStr || 'No end date'
        });
      }
    }

    const prevCount = this.changedFields.length;
    this.changedFields = changes;
    if (changes.length > 0 && prevCount === 0) {
      this.isExtendedViewOpen = true;
    }
  }

  close() {
    this.onClose.emit();
  }

  ngOnDestroy(): void { }
}
